import { CommonModule } from '@angular/common'
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core'
import { MatDialog, MatDialogConfig } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'
import { Subject, takeUntil } from 'rxjs'
import { AngularMaterialsModules } from 'src/app/module/materials.module'
import { DdcLoggerService } from 'src/app/service/log/ddc-logger.service'
import { ThemeService } from 'src/app/service/theme/theme.service'
import { UserService } from 'src/app/service/user-info/user.service'
import { WebSocketService } from 'src/app/service/websocket/websocket.service'
import { DdcDesktopDialogComponent } from 'src/app/shared/ddc/ddc-desktop-dialog/ddc-desktop-dialog.component'
import { DdcMobileSheetComponent } from 'src/app/shared/ddc/ddc-mobile-sheet/ddc-mobile-sheet.component'
import { Themes } from 'src/app/shared/enums/enums'
import {
  AddCanvasElementResult,
  AddCanvasElementsDialogComponent,
} from './add-canvas-elements-dialog/add-canvas-elements-dialog.component'
import { CanvasService } from './canvas.service'
import { CanvasCommand } from './commands/canvas-command.model'
import { DdcCanvasDiagramComponent } from './ddc-canvas-diagram/ddc-canvas-diagram.component'
import { DdcCanvasManagementToolbarComponent } from './ddc-canvas-management-toolbar/ddc-canvas-management-toolbar.component'
import { DdcCanvasSidebarComponent } from './ddc-canvas-sidebar/ddc-canvas-sidebar.component'
import { DdcCanvasToolbarComponent } from './ddc-canvas-toolbar/ddc-canvas-toolbar.component'
import {
  Canvas,
  CanvasData,
  CanvasRunEvent,
  CanvasTrace,
  TraceNodeResult,
} from './models/canvas.model'

@Component({
  selector: 'ddc-canvas',
  templateUrl: './ddc-canvas.component.html',
  styleUrls: ['./ddc-canvas.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
  imports: [
    CommonModule,
    AngularMaterialsModules,
    DdcCanvasManagementToolbarComponent,
    DdcCanvasSidebarComponent,
    DdcCanvasToolbarComponent,
    DdcCanvasDiagramComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class DdcCanvasComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>()

  @ViewChild(DdcCanvasDiagramComponent) diagramComponent: DdcCanvasDiagramComponent
  @ViewChild(DdcCanvasToolbarComponent) toolbar?: DdcCanvasToolbarComponent

  /** Supplies the current diagram data to the save command. */
  getCanvasData = (): CanvasData | null => this.diagramComponent?.getCurrentData() ?? null

  canvases: Canvas[] = []
  selectedCanvas: Canvas | null = null
  selectedCanvasData: CanvasData | null = null
  dirtyCanvasIds = new Set<number>()

  // Trace state
  traces: CanvasTrace[] = []
  selectedTrace: CanvasTrace | null = null
  isLoadingTraces = false
  isRunning = false
  nodeResults: Record<string, TraceNodeResult> = {}

  isLoading = true
  isSaving = false
  isSidebarOpen = true
  editingCanvasId: number | null = null

  _isLightTheme = true
  _isDarkTheme = false
  _isPaperTheme = false

  private _subscription: string | null = null
  private _isMobile = window.innerWidth < 1224

  private sidebarAutoHideTimer: ReturnType<typeof setTimeout> | null = null
  private readonly SIDEBAR_AUTO_HIDE_MS = 20000

  // Store original data to compare for dirty state
  private originalDataMap = new Map<number, string>()
  private isModelSettling = false

  constructor(
    private logger: DdcLoggerService,
    private canvasService: CanvasService,
    private themeService: ThemeService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private wsService: WebSocketService,
    private userService: UserService,
  ) {}

  @HostListener('window:resize')
  onResize(): void {
    this._isMobile = window.innerWidth < 1224
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardShortcut(event: KeyboardEvent): void {
    if ((event.metaKey || event.ctrlKey) && event.key === 's') {
      event.preventDefault()
      if (this.selectedCanvas && this.dirtyCanvasIds.has(this.selectedCanvas.id)) {
        this.isSaving = true
        this.toolbar?.runCommandById('save')
      }
    }
    // Cmd/Ctrl + Z → Undo
    if ((event.metaKey || event.ctrlKey) && !event.shiftKey && event.key === 'z') {
      event.preventDefault()
      if (this.diagramComponent?.canUndo) {
        this.onUndo()
      }
    }
    // Cmd/Ctrl + Shift + Z → Redo
    if ((event.metaKey || event.ctrlKey) && event.shiftKey && event.key === 'z') {
      event.preventDefault()
      if (this.diagramComponent?.canRedo) {
        this.onRedo()
      }
    }
    // Cmd/Ctrl + Shift + A → Add element
    if ((event.metaKey || event.ctrlKey) && event.shiftKey && event.key === 'A') {
      event.preventDefault()
      if (this.selectedCanvas) {
        this.onAddItem()
      }
    }
  }

  ngOnInit(): void {
    this.themeService.theme$.pipe(takeUntil(this.destroy$)).subscribe(theme => {
      this._isLightTheme = theme === Themes.LIGHT
      this._isDarkTheme = theme === Themes.DARK
      this._isPaperTheme = theme === Themes.PAPER
    })

    this.userService.userInfo$.pipe(takeUntil(this.destroy$)).subscribe((userInfo: any) => {
      this._subscription = userInfo?.subscription ?? null
    })

    this.loadCanvases()
    if (this.isSidebarOpen) {
      this.startSidebarAutoHideTimer()
    }

    // Listen for canvas run WebSocket events
    this.wsService.canvasRunEvents$.pipe(takeUntil(this.destroy$)).subscribe(msg => {
      this.handleCanvasRunEvent(msg.data as CanvasRunEvent)
    })
  }

  ngOnDestroy(): void {
    this.clearSidebarAutoHideTimer()
    this.destroy$.next()
    this.destroy$.complete()
  }

  loadCanvases(): void {
    this.isLoading = true

    this.canvasService
      .get_canvases()
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: any) => {
        this.isLoading = false
        if (res.success) {
          this.canvases = res.res
        } else {
          this.snackBar.open('Failed to load canvases', 'OK', { duration: 3000 })
        }
      })
  }

  onSelectCanvas(canvas: Canvas): void {
    this.logger.info('DdcCanvasComponent | onSelectCanvas | id: ' + canvas.id)

    // Suppress any diagram events during transition
    this.isModelSettling = true

    this.selectedCanvas = canvas
    this.selectedTrace = null
    this.nodeResults = {}

    this.canvasService
      .get_canvas(canvas.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: any) => {
        if (res.success) {
          const fullCanvas = res.res
          this.selectedCanvas = fullCanvas
          this.selectedCanvasData = fullCanvas.data ?? { nodes: [], edges: [], metadata: {} }
        } else {
          // Fallback to data already on the canvas object
          this.selectedCanvasData = canvas.data ?? { nodes: [], edges: [], metadata: {} }
        }
        this.loadTraces(canvas.id)
      })
  }

  onCreateCanvas(): void {
    this.logger.info('DdcCanvasComponent | onCreateCanvas')
    const title = 'Untitled Canvas'

    this.canvasService
      .create_canvas(title)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: any) => {
        if (res.success) {
          this.canvases = [res.res, ...this.canvases]
          this.editingCanvasId = res.res.id
          this.onSelectCanvas(res.res)
        } else {
          this.snackBar.open('Failed to create canvas', 'OK', { duration: 3000 })
        }
      })
  }

  onRenameCanvas({ canvas, title }: { canvas: Canvas; title: string }): void {
    this.logger.info(
      'DdcCanvasComponent | onRenameCanvas | id: ' + canvas.id + ' | title: ' + title,
    )
    this.editingCanvasId = null

    this.canvasService
      .rename_canvas(canvas.id, title)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: any) => {
        if (res.success) {
          canvas.title = title
          if (this.selectedCanvas?.id === canvas.id) {
            this.selectedCanvas = { ...this.selectedCanvas, title }
          }
        } else {
          this.snackBar.open('Failed to rename canvas', 'OK', { duration: 3000 })
        }
      })
  }

  /** Follow-up after a toolbar service command (save/delete) finishes. */
  onToolbarCommandExecuted(command: CanvasCommand): void {
    if (command.id === 'save') {
      this.handleSaveResult(command.lastResult)
    } else if (command.id === 'delete') {
      this.handleDeleteResult(command.lastResult)
    }
  }

  private handleSaveResult(result: { success: boolean; data?: unknown } | null): void {
    this.isSaving = false
    if (!this.selectedCanvas) return

    if (result?.success) {
      this.dirtyCanvasIds.delete(this.selectedCanvas.id)
      this.originalDataMap.set(this.selectedCanvas.id, JSON.stringify(result.data))
      this.snackBar.open('Canvas saved', 'OK', { duration: 2000 })
    } else {
      this.snackBar.open('Failed to save canvas', 'OK', { duration: 3000 })
    }
  }

  private handleDeleteResult(result: { success: boolean; data?: unknown } | null): void {
    if (result?.success) {
      const canvasId = result.data as number
      this.canvases = this.canvases.filter(c => c.id !== canvasId)
      this.dirtyCanvasIds.delete(canvasId)
      this.originalDataMap.delete(canvasId)
      this.selectedCanvas = null
      this.selectedCanvasData = null
      this.snackBar.open('Canvas deleted', 'OK', { duration: 2000 })
    } else {
      this.snackBar.open('Failed to delete canvas', 'OK', { duration: 3000 })
    }
  }

  toggleSidebar(): void {
    this.isSidebarOpen = !this.isSidebarOpen
    if (this.isSidebarOpen) {
      this.startSidebarAutoHideTimer()
    } else {
      this.clearSidebarAutoHideTimer()
    }
  }

  onSidebarInteraction(): void {
    this.startSidebarAutoHideTimer()
  }

  private startSidebarAutoHideTimer(): void {
    this.clearSidebarAutoHideTimer()
    this.sidebarAutoHideTimer = setTimeout(() => {
      this.isSidebarOpen = false
    }, this.SIDEBAR_AUTO_HIDE_MS)
  }

  private clearSidebarAutoHideTimer(): void {
    if (this.sidebarAutoHideTimer) {
      clearTimeout(this.sidebarAutoHideTimer)
      this.sidebarAutoHideTimer = null
    }
  }

  onAddItem(): void {
    this.logger.info('DdcCanvasComponent | onAddItem')
    if (!this.selectedCanvas || !this.diagramComponent) return

    const dialogRef = this.dialog.open(AddCanvasElementsDialogComponent, {
      width: '640px',
      height: '500px',
      panelClass: 'add-elements-dialog-panel',
      autoFocus: true,
    })

    dialogRef.afterClosed().subscribe((result: AddCanvasElementResult | undefined) => {
      if (result?.type && result?.data) {
        this.diagramComponent.addNodeWithData(result.type, result.data)
      }
    })
  }

  onDeleteCanvasFromSidebar(canvas: Canvas): void {
    this.logger.info('DdcCanvasComponent | onDeleteCanvasFromSidebar | id: ' + canvas.id)

    this.canvasService
      .delete_canvas(canvas.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: any) => {
        if (res.success) {
          this.canvases = this.canvases.filter(c => c.id !== canvas.id)
          this.dirtyCanvasIds.delete(canvas.id)
          this.originalDataMap.delete(canvas.id)
          if (this.selectedCanvas?.id === canvas.id) {
            this.selectedCanvas = null
            this.selectedCanvasData = null
          }
          this.snackBar.open('Canvas deleted', 'OK', { duration: 2000 })
        } else {
          this.snackBar.open('Failed to delete canvas', 'OK', { duration: 3000 })
        }
      })
  }

  onDuplicateCanvas(canvas: Canvas): void {
    this.logger.info('DdcCanvasComponent | onDuplicateCanvas | id: ' + canvas.id)

    this.canvasService
      .create_canvas(canvas.title + ' (copy)')
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: any) => {
        if (res.success) {
          const duplicate = res.res
          // Save the duplicated data to the new canvas
          const dataCopy = JSON.parse(
            JSON.stringify(canvas.data ?? { nodes: [], edges: [], metadata: {} }),
          )
          this.canvasService
            .update_canvas(duplicate.id, dataCopy)
            .pipe(takeUntil(this.destroy$))
            .subscribe((updateRes: any) => {
              if (updateRes.success) {
                duplicate.data = dataCopy
              }
              this.canvases = [duplicate, ...this.canvases]
              this.snackBar.open('Canvas duplicated', 'OK', { duration: 2000 })
            })
        } else {
          this.snackBar.open('Failed to duplicate canvas', 'OK', { duration: 3000 })
        }
      })
  }

  onDiagramChanged(): void {
    if (this.isModelSettling) return
    if (this.selectedCanvas) {
      this.checkDirtyState()
    }
  }

  onModelReady(): void {
    this.isModelSettling = false
    if (this.selectedCanvas && this.diagramComponent) {
      this.originalDataMap.set(
        this.selectedCanvas.id,
        JSON.stringify(this.diagramComponent.getCurrentData()),
      )
      this.dirtyCanvasIds.delete(this.selectedCanvas.id)
    }
  }

  onUndo(): void {
    this.diagramComponent?.undo()
  }

  onRedo(): void {
    this.diagramComponent?.redo()
  }

  private checkDirtyState(): void {
    if (!this.selectedCanvas || !this.diagramComponent) return

    const currentData = JSON.stringify(this.diagramComponent.getCurrentData())
    const originalData = this.originalDataMap.get(this.selectedCanvas.id)

    if (originalData && currentData !== originalData) {
      this.dirtyCanvasIds.add(this.selectedCanvas.id)
    } else {
      this.dirtyCanvasIds.delete(this.selectedCanvas.id)
    }
  }

  // --- Run & Traces ---

  get canRunCanvas(): boolean {
    return (
      !!this.selectedCanvas &&
      !this.isRunning &&
      !this.selectedTrace &&
      !this.dirtyCanvasIds.has(this.selectedCanvas.id)
    )
  }

  /** Only Plus and Pro subscribers may run a canvas. */
  get isSubscribed(): boolean {
    return this._subscription === 'Plus' || this._subscription === 'Pro'
  }

  /** Opens the upgrade dialog (mobile sheet or desktop dialog) when a free-tier user tries to run. */
  openUpgradeDialog(): void {
    if (this._isMobile) {
      const dialogConfig = new MatDialogConfig()
      const themeClass = this._isDarkTheme
        ? Themes.DARK
        : this._isPaperTheme
          ? Themes.PAPER
          : Themes.LIGHT
      dialogConfig.panelClass = ['mobile-sheet-container', themeClass]
      dialogConfig.position = { bottom: '0' }
      dialogConfig.width = '100%'
      dialogConfig.height = '80vh'
      dialogConfig.maxWidth = '100vw'
      dialogConfig.hasBackdrop = true
      dialogConfig.backdropClass = 'mobile-sheet-backdrop'
      dialogConfig.enterAnimationDuration = '300ms'
      dialogConfig.exitAnimationDuration = '200ms'
      dialogConfig.data = { type: 'upgrade' }
      this.dialog.open(DdcMobileSheetComponent, dialogConfig)
    } else {
      const dialogConfig = new MatDialogConfig()
      dialogConfig.width = '600px'
      dialogConfig.maxWidth = '90vw'
      dialogConfig.hasBackdrop = true
      dialogConfig.data = { type: 'upgrade' }
      this.dialog.open(DdcDesktopDialogComponent, dialogConfig)
    }
  }

  onRunCanvas(): void {
    if (!this.selectedCanvas || this.isRunning) return

    if (!this.isSubscribed) {
      this.openUpgradeDialog()
      return
    }

    this.logger.info('DdcCanvasComponent | onRunCanvas | id: ' + this.selectedCanvas.id)
    this.isRunning = true
    this.nodeResults = {}
    this.selectedTrace = null

    this.canvasService
      .run_canvas(this.selectedCanvas.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: any) => {
        this.isRunning = false
        if (res.success) {
          const trace: CanvasTrace = res.res
          this.nodeResults = trace.node_results ?? {}
          this.selectedTrace = trace
          this.loadTraces(this.selectedCanvas!.id)
          this.snackBar.open('Canvas run completed', 'OK', { duration: 3000 })
        } else {
          this.snackBar.open('Canvas run failed: ' + (res.res || 'Unknown error'), 'OK', {
            duration: 4000,
          })
        }
      })
  }

  loadTraces(canvasId: number): void {
    this.isLoadingTraces = true
    this.canvasService
      .get_traces(canvasId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: any) => {
        this.isLoadingTraces = false
        if (res.success) {
          this.traces = res.res
        }
      })
  }

  onSelectTrace(trace: CanvasTrace): void {
    this.logger.info('DdcCanvasComponent | onSelectTrace | id: ' + trace.id)
    this.selectedTrace = trace
    this.nodeResults = trace.node_results ?? {}
  }

  onDeleteTrace(trace: CanvasTrace): void {
    if (!this.selectedCanvas) return

    this.canvasService
      .delete_trace(this.selectedCanvas.id, trace.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: any) => {
        if (res.success) {
          this.traces = this.traces.filter(t => t.id !== trace.id)
          if (this.selectedTrace?.id === trace.id) {
            this.selectedTrace = null
            this.nodeResults = {}
          }
          this.snackBar.open('Trace deleted', 'OK', { duration: 2000 })
        } else {
          this.snackBar.open('Failed to delete trace', 'OK', { duration: 3000 })
        }
      })
  }

  onDeleteAllTraces(): void {
    if (!this.selectedCanvas || this.traces.length === 0) return

    const canvasId = this.selectedCanvas.id
    const traceIds = this.traces.map(t => t.id)
    let remaining = traceIds.length

    for (const traceId of traceIds) {
      this.canvasService
        .delete_trace(canvasId, traceId)
        .pipe(takeUntil(this.destroy$))
        .subscribe((res: any) => {
          remaining--
          if (remaining === 0) {
            this.traces = []
            this.selectedTrace = null
            this.nodeResults = {}
            this.snackBar.open('All traces deleted', 'OK', { duration: 2000 })
          }
        })
    }
  }

  onClearTrace(): void {
    if (this.selectedTrace) {
      this.onDeleteTrace(this.selectedTrace)
    }
  }

  onExitTrace(): void {
    this.selectedTrace = null
    this.nodeResults = {}
  }

  private handleCanvasRunEvent(event: CanvasRunEvent): void {
    if (!this.selectedCanvas || event.canvas_id !== this.selectedCanvas.id) return

    switch (event.event) {
      case 'run_started':
        this.isRunning = true
        this.nodeResults = {}
        break

      case 'node_started':
        if (event.node_id) {
          this.nodeResults = {
            ...this.nodeResults,
            [event.node_id]: {
              status: 'running',
              node_type: event.node_type ?? '',
              incoming_edges: event.incoming_edges ?? [],
            },
          }
        }
        break

      case 'node_completed':
        if (event.node_id && event.result) {
          this.nodeResults = {
            ...this.nodeResults,
            [event.node_id]: event.result,
          }
        }
        break

      case 'node_failed':
        if (event.node_id) {
          this.nodeResults = {
            ...this.nodeResults,
            [event.node_id]: {
              status: 'error',
              node_type: event.node_type ?? '',
              error: event.error,
              incoming_edges: event.incoming_edges ?? [],
            },
          }
        }
        break

      case 'run_completed':
      case 'run_failed':
        this.isRunning = false
        if (event.node_results) {
          this.nodeResults = event.node_results
        }
        this.loadTraces(this.selectedCanvas.id)
        break
    }
  }
}
